import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, CreditCard, FileText, MapPin, MessageCircle, Ship } from "lucide-react";
import { useDemo } from "@/store/DemoStore";
import { CURRENT_CLIENT_ID } from "@/data/constants";
import { Avatar, Card, avatarUrl } from "@/components/common";
import { ShareTripButton, InviteFriendsButton } from "@/components/TripShare";
import { daysUntil, fmtDate, fmtMoney, fmtShort, nextPayment, returnDate, tripTotal } from "@/lib/trip";

const greet = () => { const h = new Date().getHours(); return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening"; };

const OVERLAY_BTN = "inline-flex items-center gap-1.5 rounded-full bg-white/90 px-3 py-1.5 text-xs font-semibold text-navy-900 shadow-sm backdrop-blur transition-colors hover:bg-white disabled:opacity-70";
const MOBILE_BTN = "inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white py-2.5 text-sm font-semibold text-navy-900 transition-colors hover:bg-slate-50 disabled:opacity-70";

function NextTrip({ trip }) {
  const d = daysUntil(trip.departure);
  return (
    <Card className="overflow-hidden" data-testid="client-next-trip">
      <div className="relative h-48 sm:h-64">
        <Link to={`/mytrip/trips/${trip.id}`} className="absolute inset-0 block" data-testid="client-next-trip-link">
          <img src={trip.image || "/images/sunset.jpg"} alt={trip.destination} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy-950/85 via-navy-950/20 to-transparent" />
          <span className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-navy-900 backdrop-blur">Next trip</span>
          <div className="absolute bottom-4 left-4 right-4 text-white">
            <h2 className="font-display text-xl font-bold sm:text-2xl">{trip.title}</h2>
            <p className="mt-0.5 flex items-center gap-1.5 text-sm text-white/80"><Ship className="h-3.5 w-3.5" />{trip.vessel} · {fmtShort(trip.departure)} – {fmtDate(returnDate(trip))}</p>
          </div>
        </Link>
        <div className="absolute right-4 top-4 z-10 hidden items-center gap-2 sm:flex">
          <ShareTripButton trip={trip} className={OVERLAY_BTN} />
          <InviteFriendsButton trip={trip} className={OVERLAY_BTN} />
        </div>
      </div>
      <div className="grid grid-cols-3 divide-x divide-slate-100 py-4" data-testid="client-countdown">
        {[[d, d === 1 ? "Day to go" : "Days to go"], [fmtShort(trip.departure), "Departure"], [`${trip.nights}`, "Nights"]].map(([v, l]) => (
          <div key={l} className="min-w-0 px-2 text-center"><p className="truncate font-display text-2xl font-bold text-navy-900 sm:text-3xl">{v}</p><p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">{l}</p></div>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2 border-t border-slate-100 p-3 sm:hidden">
        <ShareTripButton trip={trip} className={MOBILE_BTN} />
        <InviteFriendsButton trip={trip} className={MOBILE_BTN} />
      </div>
    </Card>
  );
}

const EMPTY_TRIP_IMG = "https://images.unsplash.com/photo-1505142468610-359e7d316be0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1280";

function NoTripYet({ advisorFirst }) {
  return (
    <Card className="overflow-hidden" data-testid="client-no-trip">
      <div className="relative h-48 sm:h-64">
        <img src={EMPTY_TRIP_IMG} alt="Dream destination" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy-950/90 via-navy-950/40 to-navy-950/10" />
        <span className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-navy-900 backdrop-blur">Your next adventure</span>
        <div className="absolute bottom-4 left-4 right-4 text-white">
          <h2 className="font-display text-xl font-bold sm:text-2xl">Where to next?</h2>
          <p className="mt-0.5 max-w-md text-sm text-white/80">You have no trips booked yet. Reach out and we'll start planning something unforgettable.</p>
        </div>
      </div>
      <div className="p-4">
        <Link to="/mytrip/messages" data-testid="client-no-trip-cta" className="flex w-full items-center justify-center gap-2 rounded-xl bg-navy-900 py-3 text-sm font-semibold text-white transition-colors hover:bg-navy-800">
          <MessageCircle className="h-4 w-4" />Start planning with {advisorFirst}
        </Link>
      </div>
    </Card>
  );
}

function Shortcut({ to, onClick, icon: Icon, label, sub, testId }) {
  const cls = "flex items-center gap-3 rounded-2xl border border-slate-200/80 bg-white p-4 text-left transition-shadow hover:shadow-[0_6px_18px_rgba(15,30,54,0.07)]";
  const body = (<><span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-50 text-navy-800"><Icon className="h-5 w-5" strokeWidth={1.75} /></span><span className="min-w-0"><span className="block text-sm font-semibold text-navy-900">{label}</span><span className="block truncate text-xs text-slate-500">{sub}</span></span></>);
  return to ? <Link to={to} className={cls} data-testid={testId}>{body}</Link> : <button onClick={onClick} className={cls} data-testid={testId}>{body}</button>;
}

export default function ClientHome() {
  const { client, tripsFor, settings, paidExtra } = useDemo();
  const me = client(CURRENT_CLIENT_ID);
  const trips = tripsFor(me.id);
  const next = trips.filter((t) => t.status === "BOOKED" && daysUntil(t.departure) >= 0).sort((a, b) => a.departure.localeCompare(b.departure))[0];
  const quote = trips.find((t) => t.status === "QUOTE");
  const pay = next && nextPayment(next, paidExtra(next.id));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-[0.16em] text-slate-400">{new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</p>
          <h1 className="mt-1 font-display text-2xl font-bold tracking-tight text-navy-900 sm:text-3xl" data-testid="client-greeting">{greet()}, {me.name.split(" ")[0]}</h1>
        </div>
        <Link to="/mytrip/profile" data-testid="client-avatar-link"><Avatar name={me.name} /></Link>
      </div>
      {next ? <NextTrip trip={next} /> : <NoTripYet advisorFirst={settings.advisorName.split(" ")[0]} />}
      {next && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="client-shortcuts">
          <Shortcut testId="shortcut-itinerary" to={`/mytrip/trips/${next.id}?tab=itinerary`} icon={CalendarDays} label="Itinerary" sub={`${next.nights + 1} days`} />
          <Shortcut testId="shortcut-payments" to={`/mytrip/trips/${next.id}?tab=payments`} icon={CreditCard} label="Payments" sub={pay ? `${fmtMoney(pay.amount)} due` : "Paid in full"} />
          <Shortcut testId="shortcut-documents" to={`/mytrip/trips/${next.id}?tab=documents`} icon={FileText} label="Documents" sub="Travel documents" />
          <Shortcut testId="shortcut-message" to="/mytrip/messages" icon={MessageCircle} label="Message" sub={settings.advisorName.split(" ")[0]} />
        </div>
      )}
      {pay && (
        <Card className="flex items-center gap-4 border-amber-200 bg-amber-50/60 p-4" data-testid="client-payment-reminder">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-white text-amber-600"><CreditCard className="h-5 w-5" /></span>
          <div className="min-w-0 flex-1"><p className="text-sm font-semibold text-navy-900">{pay.label}: {fmtMoney(pay.amount)}</p><p className="text-xs text-slate-600">Due {fmtDate(pay.due)} · {next.bookingNumber}</p></div>
          <Link to={`/mytrip/trips/${next.id}?tab=payments`} className="shrink-0 text-sm font-semibold text-navy-900" data-testid="client-payment-view">View</Link>
        </Card>
      )}
      {quote && (
        <Link to={`/mytrip/trips/${quote.id}`} data-testid="client-quote-card" className="group flex items-center gap-4 rounded-2xl border border-slate-200/80 bg-white p-3 pr-4">
          <img src={quote.image} alt="" className="h-16 w-20 shrink-0 rounded-xl object-cover" />
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-amber-600">Quote ready</p>
            <p className="truncate text-sm font-semibold text-navy-900">{quote.title}</p>
            <p className="truncate text-xs text-slate-500"><MapPin className="mr-1 inline h-3 w-3" />{quote.vessel} · {fmtMoney(tripTotal(quote))}</p>
          </div>
          <ArrowRight className="h-4 w-4 shrink-0 text-slate-300 group-hover:text-navy-900" />
        </Link>
      )}
      <Card className="flex items-center gap-4 p-4" data-testid="client-advisor-card">
        <Avatar name={settings.advisorName} src={avatarUrl(settings.advisorId, settings.advisorAvatar)} size="lg" />
        <div className="min-w-0 flex-1"><p className="text-xs text-slate-500">Your travel advisor</p><p className="font-semibold text-navy-900">{settings.advisorName}</p><p className="truncate text-xs text-slate-500">{settings.agencyName}</p></div>
        <Link to="/mytrip/messages" className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-navy-900 text-white" aria-label="Message advisor" data-testid="client-advisor-message-btn"><MessageCircle className="h-5 w-5" /></Link>
      </Card>
    </div>
  );
}
